import { useState, useRef, useCallback, useEffect } from 'react'
import { Card, CardHeader, CardTitle, CardContent, Button, Input } from '@/components/ui'
import { PenTool, Upload, Download, Trash2, Type, Paintbrush } from 'lucide-react'
import { toast } from 'sonner'
import { PDFDocument } from 'pdf-lib'

type Tab = 'draw' | 'type' | 'upload'

const SIGNATURE_FONTS = [
  { name: 'Cursive', style: "'Dancing Script', cursive" },
  { name: 'Elegant', style: "'Great Vibes', cursive" },
  { name: 'Handwritten', style: "'Caveat', cursive" },
  { name: 'Classic', style: "'Pacifico', cursive" },
  { name: 'Formal', style: "'Allura', cursive" },
  { name: 'Bold Script', style: "'Satisfy', cursive" },
]

// Load Google Fonts for signature styles
const fontLink = document.createElement('link')
fontLink.rel = 'stylesheet'
fontLink.href = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&family=Great+Vibes&family=Caveat:wght@700&family=Pacifico&family=Allura&family=Satisfy&display=swap'
document.head.appendChild(fontLink)

export function ESignature() {
  const [tab, setTab] = useState<Tab>('draw')
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null)
  const [pdfFile, setPdfFile] = useState<File | null>(null)
  const [pdfPages, setPdfPages] = useState<string[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [sigPosition, setSigPosition] = useState<{ x: number; y: number } | null>(null)
  const [sigSize, setSigSize] = useState(150)
  const [isDragging, setIsDragging] = useState(false)
  const [typedName, setTypedName] = useState('')
  const [selectedFont, setSelectedFont] = useState(SIGNATURE_FONTS[0])
  const [signatureColor, setSignatureColor] = useState('#000000')
  const [uploadedSig, setUploadedSig] = useState<string | null>(null)

  // Canvas drawing state
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [hasDrawn, setHasDrawn] = useState(false)
  const [strokeWidth, setStrokeWidth] = useState(2.5)
  const pdfContainerRef = useRef<HTMLDivElement>(null)

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.strokeStyle = signatureColor
    ctx.lineWidth = strokeWidth
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
  }, [tab, signatureColor, strokeWidth])

  const getCanvasCoords = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    if ('touches' in e) {
      const touch = e.touches[0]
      return { x: (touch.clientX - rect.left) * scaleX, y: (touch.clientY - rect.top) * scaleY }
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY }
  }

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    const { x, y } = getCanvasCoords(e)
    ctx.beginPath()
    ctx.moveTo(x, y)
    setIsDrawing(true)
    setHasDrawn(true)
  }

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return
    e.preventDefault()
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    ctx.strokeStyle = signatureColor
    ctx.lineWidth = strokeWidth
    const { x, y } = getCanvasCoords(e)
    ctx.lineTo(x, y)
    ctx.stroke()
  }

  const endDraw = () => setIsDrawing(false)

  const clearCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    setHasDrawn(false)
  }

  // Save drawn signature
  const saveDrawnSignature = () => {
    if (!canvasRef.current || !hasDrawn) {
      toast.error('Draw your signature first')
      return
    }
    setSignatureDataUrl(canvasRef.current.toDataURL('image/png'))
    toast.success('Signature saved')
  }

  // Generate typed signature as image
  const saveTypedSignature = () => {
    if (!typedName.trim()) {
      toast.error('Type your name first')
      return
    }
    const canvas = document.createElement('canvas')
    canvas.width = 500
    canvas.height = 150
    const ctx = canvas.getContext('2d')!
    ctx.clearRect(0, 0, 500, 150)
    ctx.fillStyle = signatureColor
    ctx.font = `48px ${selectedFont.style}`
    ctx.textBaseline = 'middle'
    ctx.fillText(typedName, 20, 75)
    setSignatureDataUrl(canvas.toDataURL('image/png'))
    toast.success('Signature saved')
  }

  // Upload signature image
  const handleSigUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      setUploadedSig(reader.result as string)
      setSignatureDataUrl(reader.result as string)
      toast.success('Signature image loaded')
    }
    reader.readAsDataURL(file)
  }

  // PDF upload and rendering
  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file')
      return
    }
    setPdfFile(file)
    setSigPosition(null)
    setCurrentPage(0)

    const arrayBuffer = await file.arrayBuffer()
    const pdfjsLib = await import('pdfjs-dist')
    pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).href

    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
    const pages: string[] = []

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i)
      const viewport = page.getViewport({ scale: 1.5 })
      const canvas = document.createElement('canvas')
      canvas.width = viewport.width
      canvas.height = viewport.height
      const ctx = canvas.getContext('2d')!
      await page.render({ canvasContext: ctx, viewport, canvas }).promise
      pages.push(canvas.toDataURL('image/png'))
    }

    setPdfPages(pages)
    toast.success(`PDF loaded (${pdf.numPages} page${pdf.numPages > 1 ? 's' : ''})`)
  }

  // Click on PDF to place signature
  const handlePdfClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!signatureDataUrl) {
      toast.error('Create or upload a signature first')
      return
    }
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    setSigPosition({ x, y })
  }

  // Drag signature
  const handleSigMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleSigMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !pdfContainerRef.current) return
    const rect = pdfContainerRef.current.getBoundingClientRect()
    setSigPosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    })
  }, [isDragging])

  const handleSigMouseUp = useCallback(() => setIsDragging(false), [])

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleSigMouseMove)
      window.addEventListener('mouseup', handleSigMouseUp)
    }
    return () => {
      window.removeEventListener('mousemove', handleSigMouseMove)
      window.removeEventListener('mouseup', handleSigMouseUp)
    }
  }, [isDragging, handleSigMouseMove, handleSigMouseUp])

  // Apply signature and download
  const applySignature = async () => {
    if (!pdfFile || !signatureDataUrl || !sigPosition) {
      toast.error('Upload a PDF, create a signature, and place it on the document')
      return
    }

    const arrayBuffer = await pdfFile.arrayBuffer()
    const pdfDoc = await PDFDocument.load(arrayBuffer)
    const page = pdfDoc.getPages()[currentPage]

    // Convert screen coords to PDF coords
    const container = pdfContainerRef.current
    if (!container) return
    const img = container.querySelector('img')
    if (!img) return

    const displayWidth = img.clientWidth
    const displayHeight = img.clientHeight
    const pdfWidth = page.getWidth()
    const pdfHeight = page.getHeight()
    const scaleX = pdfWidth / displayWidth
    const scaleY = pdfHeight / displayHeight

    const sigImage = await pdfDoc.embedPng(await fetchAsUint8(signatureDataUrl))
    const sigW = sigSize * scaleX
    const sigH = (sigSize * (sigImage.height / sigImage.width)) * scaleX
    const pdfX = (sigPosition.x - sigSize / 2) * scaleX
    const pdfY = pdfHeight - (sigPosition.y * scaleY) - sigH / 2

    page.drawImage(sigImage, { x: pdfX, y: pdfY, width: sigW, height: sigH })

    const pdfBytes = await pdfDoc.save()
    const blob = new Blob([pdfBytes as unknown as BlobPart], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `signed_${pdfFile.name}`
    a.click()
    URL.revokeObjectURL(url)
    toast.success('Signed PDF downloaded')
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <PenTool className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">E-Signature</h1>
            <p className="text-muted-foreground">Create signatures and sign PDF documents</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[400px_1fr]">
        {/* Left: Signature Creation */}
        <div className="space-y-4">
          <Card className="glass">
            <CardHeader>
              <CardTitle className="text-base">Create Signature</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Tabs */}
              <div className="flex gap-1 rounded-lg bg-muted p-1">
                {([
                  { id: 'draw' as Tab, label: 'Draw', icon: Paintbrush },
                  { id: 'type' as Tab, label: 'Type', icon: Type },
                  { id: 'upload' as Tab, label: 'Upload', icon: Upload },
                ]).map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setTab(id)}
                    className={`flex-1 flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                      tab === id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {label}
                  </button>
                ))}
              </div>

              {/* Color picker */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-muted-foreground">Color:</label>
                <div className="flex gap-1.5">
                  {['#000000', '#1e40af', '#dc2626', '#166534'].map((color) => (
                    <button
                      key={color}
                      onClick={() => setSignatureColor(color)}
                      className={`h-6 w-6 rounded-full border-2 transition-transform ${
                        signatureColor === color ? 'border-primary scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              {/* Draw tab */}
              {tab === 'draw' && (
                <div className="space-y-3">
                  <canvas
                    ref={canvasRef}
                    width={500}
                    height={150}
                    className="w-full rounded-md border border-border cursor-crosshair touch-none"
                    style={{ background: 'repeating-conic-gradient(#d4d4d4 0% 25%, #fff 0% 50%) 0 0 / 16px 16px' }}
                    onMouseDown={startDraw}
                    onMouseMove={draw}
                    onMouseUp={endDraw}
                    onMouseLeave={endDraw}
                    onTouchStart={startDraw}
                    onTouchMove={draw}
                    onTouchEnd={endDraw}
                  />
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <label className="text-xs text-muted-foreground">Thickness</label>
                      <span className="text-xs text-muted-foreground font-mono">{strokeWidth.toFixed(1)}px</span>
                    </div>
                    <input
                      type="range"
                      min={0.5}
                      max={8}
                      step={0.5}
                      value={strokeWidth}
                      onChange={(e) => setStrokeWidth(Number(e.target.value))}
                      className="w-full"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={clearCanvas} className="flex-1">
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Clear
                    </Button>
                    <Button size="sm" onClick={saveDrawnSignature} className="flex-1">
                      Save Signature
                    </Button>
                  </div>
                </div>
              )}

              {/* Type tab */}
              {tab === 'type' && (
                <div className="space-y-3">
                  <Input
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    placeholder="Type your name..."
                    className="font-mono"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    {SIGNATURE_FONTS.map((font) => (
                      <button
                        key={font.name}
                        onClick={() => setSelectedFont(font)}
                        className={`rounded-md border p-3 text-left transition-colors ${
                          selectedFont.name === font.name
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <span className="text-[10px] text-muted-foreground block mb-1">{font.name}</span>
                        <span
                          className="text-lg block truncate"
                          style={{ fontFamily: font.style, color: signatureColor }}
                        >
                          {typedName || 'Your Name'}
                        </span>
                      </button>
                    ))}
                  </div>
                  <Button size="sm" onClick={saveTypedSignature} className="w-full">
                    Save Signature
                  </Button>
                </div>
              )}

              {/* Upload tab */}
              {tab === 'upload' && (
                <div className="space-y-3">
                  <label className="flex flex-col items-center gap-2 rounded-md border-2 border-dashed border-border p-6 cursor-pointer hover:border-primary/50 transition-colors">
                    <Upload className="h-8 w-8 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Upload signature image (PNG)</span>
                    <input type="file" accept="image/*" className="hidden" onChange={handleSigUpload} />
                  </label>
                  {uploadedSig && (
                    <div className="rounded-md border border-border p-2" style={{ background: 'repeating-conic-gradient(#d4d4d4 0% 25%, #fff 0% 50%) 0 0 / 12px 12px' }}>
                      <img src={uploadedSig} alt="Uploaded signature" className="max-h-20 mx-auto" />
                    </div>
                  )}
                </div>
              )}

              {/* Signature preview */}
              {signatureDataUrl && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground font-medium">Current Signature:</p>
                  <div className="rounded-md border border-border p-2" style={{ background: 'repeating-conic-gradient(#d4d4d4 0% 25%, #fff 0% 50%) 0 0 / 12px 12px' }}>
                    <img src={signatureDataUrl} alt="Signature" className="max-h-16 mx-auto" />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Signature size */}
          {signatureDataUrl && pdfPages.length > 0 && (
            <Card className="glass">
              <CardContent className="p-4 space-y-2">
                <label className="text-sm text-muted-foreground">Signature size: {sigSize}px</label>
                <input
                  type="range"
                  min={50}
                  max={300}
                  value={sigSize}
                  onChange={(e) => setSigSize(Number(e.target.value))}
                  className="w-full"
                />
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: PDF viewer */}
        <div className="space-y-4">
          <Card className="glass">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">PDF Document</CardTitle>
                <div className="flex gap-2">
                  {pdfPages.length > 0 && sigPosition && signatureDataUrl && (
                    <Button size="sm" onClick={applySignature}>
                      <Download className="h-3.5 w-3.5 mr-1.5" /> Download Signed PDF
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {pdfPages.length === 0 ? (
                <label className="flex flex-col items-center gap-3 rounded-lg border-2 border-dashed border-border p-12 cursor-pointer hover:border-primary/50 transition-colors">
                  <Upload className="h-12 w-12 text-muted-foreground" />
                  <span className="text-muted-foreground">Upload a PDF file to sign</span>
                  <span className="text-xs text-muted-foreground">Click or drag and drop</span>
                  <input type="file" accept=".pdf" className="hidden" onChange={handlePdfUpload} />
                </label>
              ) : (
                <div className="space-y-3">
                  {/* Page navigation */}
                  {pdfPages.length > 1 && (
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage === 0}
                        onClick={() => { setCurrentPage((p) => p - 1); setSigPosition(null) }}
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Page {currentPage + 1} of {pdfPages.length}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage === pdfPages.length - 1}
                        onClick={() => { setCurrentPage((p) => p + 1); setSigPosition(null) }}
                      >
                        Next
                      </Button>
                    </div>
                  )}

                  {/* PDF page with signature overlay */}
                  <div
                    ref={pdfContainerRef}
                    className="relative border border-border rounded-md overflow-hidden cursor-crosshair select-none"
                    onClick={handlePdfClick}
                  >
                    <img
                      src={pdfPages[currentPage]}
                      alt={`Page ${currentPage + 1}`}
                      className="w-full pointer-events-none"
                      draggable={false}
                    />
                    {sigPosition && signatureDataUrl && (
                      <img
                        src={signatureDataUrl}
                        alt="Signature"
                        className="absolute pointer-events-auto cursor-grab active:cursor-grabbing"
                        style={{
                          left: sigPosition.x - sigSize / 2,
                          top: sigPosition.y - sigSize / 4,
                          width: sigSize,
                          border: '2px dashed rgba(59, 130, 246, 0.5)',
                          borderRadius: 4,
                        }}
                        onMouseDown={handleSigMouseDown}
                        draggable={false}
                      />
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground text-center">
                    Click on the document to place your signature. Drag to reposition.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Info */}
      <Card className="glass">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 shrink-0">
              <PenTool className="h-4 w-4 text-primary" />
            </div>
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">About E-Signature</p>
              <p>
                Create your signature by drawing, typing with signature fonts, or uploading an image.
                Then upload a PDF, click to place your signature, and download the signed document.
                Everything runs locally in your browser — no files are uploaded to any server.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

async function fetchAsUint8(dataUrl: string): Promise<Uint8Array> {
  const res = await fetch(dataUrl)
  const buf = await res.arrayBuffer()
  return new Uint8Array(buf)
}
